/**
 * 2D affine matrices in the convention mandated by docs/04_EDITOR_BEHAVIOR.md:
 * `[a, b, c, d, e, f]` maps `(x, y)` to `(a*x + c*y + e, b*x + d*y + f)`.
 * Parent transforms multiply child transforms on the left: `world = parent × child`.
 */
export type Matrix = readonly [a: number, b: number, c: number, d: number, e: number, f: number];

export interface Point {
  readonly x: number;
  readonly y: number;
}

export const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

/** Determinants at or below this magnitude are treated as singular (matches the handoff validator). */
export const SINGULAR_EPSILON = 1e-12;

/** Returns `m × n`: the result applies `n` first, then `m`. */
export function multiply(m: Matrix, n: Matrix): Matrix {
  const [a, b, c, d, e, f] = m;
  const [a2, b2, c2, d2, e2, f2] = n;
  return [
    a * a2 + c * b2,
    b * a2 + d * b2,
    a * c2 + c * d2,
    b * c2 + d * d2,
    a * e2 + c * f2 + e,
    b * e2 + d * f2 + f,
  ];
}

/** Composes matrices so that the last argument is applied first: `compose(A, B, C) = A × B × C`. */
export function compose(...matrices: readonly Matrix[]): Matrix {
  let result: Matrix = IDENTITY;
  for (const m of matrices) result = multiply(result, m);
  return result;
}

export function applyToPoint(m: Matrix, p: Point): Point {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] };
}

/** Applies only the linear part (no translation), e.g. to direction vectors. */
export function applyToVector(m: Matrix, v: Point): Point {
  return { x: m[0] * v.x + m[2] * v.y, y: m[1] * v.x + m[3] * v.y };
}

export function determinant(m: Matrix): number {
  return m[0] * m[3] - m[1] * m[2];
}

export function isFiniteMatrix(m: readonly number[]): m is Matrix {
  return m.length === 6 && m.every((v) => Number.isFinite(v));
}

export function isSingular(m: Matrix, epsilon = SINGULAR_EPSILON): boolean {
  return Math.abs(determinant(m)) <= epsilon;
}

/** Inverse of `m`, or `null` when `m` is singular or not finite. */
export function invert(m: Matrix): Matrix | null {
  if (!isFiniteMatrix(m) || isSingular(m)) return null;
  const [a, b, c, d, e, f] = m;
  const det = a * d - b * c;
  return [d / det, -b / det, -c / det, a / det, (c * f - d * e) / det, (b * e - a * f) / det];
}

export function translation(tx: number, ty: number): Matrix {
  return [1, 0, 0, 1, tx, ty];
}

export function scaling(sx: number, sy: number = sx): Matrix {
  return [sx, 0, 0, sy, 0, 0];
}

/** Rotation by `degrees`; positive angles turn +X toward +Y (clockwise on a Y-down artboard). */
export function rotation(degrees: number): Matrix {
  const r = (degrees * Math.PI) / 180;
  const cos = cleanTrig(Math.cos(r));
  const sin = cleanTrig(Math.sin(r));
  return [cos, sin, -sin, cos, 0, 0];
}

/** Conjugates `m` so that it acts around `origin` instead of (0, 0). */
export function about(origin: Point, m: Matrix): Matrix {
  return compose(translation(origin.x, origin.y), m, translation(-origin.x, -origin.y));
}

/** Geometric-mean linear scale factor `sqrt(|det(A)|)` (docs/04 stroke scaling contract). */
export function geometricMeanScale(m: Matrix): number {
  return Math.sqrt(Math.abs(determinant(m)));
}

export interface Decomposition {
  readonly translateX: number;
  readonly translateY: number;
  /** Rotation of the local +X axis, in degrees within (-180, 180]. */
  readonly rotation: number;
  /** Length of the transformed local +X unit vector (always positive). */
  readonly scaleX: number;
  /** Signed local Y scale; negative when the matrix contains a reflection. */
  readonly scaleY: number;
  /** Shear factor of the upper-triangular part (0 for pure rotate/scale). */
  readonly shear: number;
}

/**
 * Decomposes `m` as `T × R(rotation) × [[scaleX, shear], [0, scaleY]]`.
 * Exact for every nonsingular matrix; `recompose(decompose(m))` reproduces `m`.
 */
export function decompose(m: Matrix): Decomposition {
  const [a, b, c, d, e, f] = m;
  const scaleX = Math.hypot(a, b);
  if (scaleX === 0) {
    return { translateX: e, translateY: f, rotation: 0, scaleX: 0, scaleY: 0, shear: 0 };
  }
  const angle = Math.atan2(b, a);
  const cos = a / scaleX;
  const sin = b / scaleX;
  return {
    translateX: e,
    translateY: f,
    rotation: normalizeDegrees((angle * 180) / Math.PI),
    scaleX,
    scaleY: -c * sin + d * cos,
    shear: c * cos + d * sin,
  };
}

export function recompose(p: Decomposition): Matrix {
  const r = (p.rotation * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  // R × U where U = [[sx, sh], [0, sy]] in column-vector form.
  return [
    cos * p.scaleX,
    sin * p.scaleX,
    cos * p.shear - sin * p.scaleY,
    sin * p.shear + cos * p.scaleY,
    p.translateX,
    p.translateY,
  ];
}

/** Normalizes an angle to the half-open interval (-180, 180]. */
export function normalizeDegrees(degrees: number): number {
  let d = degrees % 360;
  if (d <= -180) d += 360;
  if (d > 180) d -= 360;
  return d === 0 ? 0 : d; // collapse -0
}

export function matricesEqual(m: Matrix, n: Matrix, tolerance = 1e-9): boolean {
  return m.every((v, i) => Math.abs(v - (n[i] ?? Number.NaN)) <= tolerance);
}

/** Removes floating-point noise from exact trigonometric values (cos 90° = 6e-17). */
function cleanTrig(value: number): number {
  return Math.abs(value) < 1e-15 ? 0 : value;
}
